import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useAuth } from "@/contexts/auth-context";

interface MyBusiness {
  id: string;
  name: string;
  slug: string;
  status: string;
  business_role: string | null;
  logo_url: string | null;
  icon_url: string | null;
}

export function resolveLogoSrc(
  biz: Pick<MyBusiness, "icon_url" | "logo_url">,
): string | undefined {
  return biz.icon_url ?? biz.logo_url ?? undefined;
}

interface MyBusinessesResponse {
  businesses: MyBusiness[];
  active_business_id: string;
}

interface SwitchBusinessResponse {
  success: boolean;
  business_id: string;
}

// `enabled` gates the bearer-protected /core/me/businesses fetch. Callers that
// can render before a session exists (e.g. RumIdentitySync, mounted above the
// router) must pass `enabled: false` while anonymous so an unauthenticated 401
// does not trigger apiFetch's signOut()+redirect on public routes (ADV-1319).
export function useMyBusinesses(enabled = true) {
  return useQuery({
    queryKey: ["my-businesses"],
    queryFn: () => api.get<MyBusinessesResponse>("/core/me/businesses"),
    enabled,
  });
}

export function useSwitchBusiness() {
  const queryClient = useQueryClient();
  const { refreshSession } = useAuth();

  return useMutation({
    mutationFn: (businessId: string) =>
      api.post<SwitchBusinessResponse>("/core/me/switch", {
        business_id: businessId,
      }),
    onSuccess: async () => {
      await refreshSession();
      queryClient.invalidateQueries();
    },
  });
}
